// src/components/onboarding/ReceiptPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  continueOnboarding,
  getReceipt,
} from "../../shared/_services/onboarding.services";
import StarsIcon from "../../styles/StarsIcon";
import { Badge, Button, Panel, cn } from "../ui";
import Loader from "../Loader";
import OnboardingLayout from "./OnboardingLayout";
import {
  AGREEMENT_SIGNED,
  GENERIC_ERROR,
  LOAD_FAILED,
  PILL_DELIVERED,
  PILL_SIGNED,
  RECEIPT_ACKNOWLEDGED,
  RECEIPT_CONSENTS_HEADING,
  RECEIPT_CONTINUE,
  RECEIPT_DOCS_HEADING,
  RECEIPT_H1,
  RECEIPT_INTRO,
  RECEIPT_NEXT,
  RECEIPT_NOT_YET,
  RECEIPT_SEE_DOCUMENTS,
  RECEIPT_SIDE_TITLE,
  TRY_AGAIN,
} from "./onboardingCopy";
import { clockTime, fullDate, railLabelFor } from "./onboardingFormat";
import { isWrongStage, useStageNavigation } from "./onboardingNav";

// A receipt row's line: the latest signature for the agreement, the
// acknowledgement (and edition) for a delivered document.
const docLine = (row) =>
  row.status === "signed"
    ? AGREEMENT_SIGNED({
        date: fullDate(row.at),
        time: clockTime(row.at),
        versionLabel: row.versionLabel,
        typedName: row.typedName,
      })
    : RECEIPT_ACKNOWLEDGED(fullDate(row.at), row.editionLabel);

function DocRow({ row, child = false }) {
  return (
    <li
      data-slot="onb-receipt-row"
      data-child={child ? "true" : undefined}
      className={cn(
        "tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-3 last:tw-border-b-0",
        child && "tw-pl-6",
      )}
    >
      <div>
        <div className="tw-text-sm tw-font-semibold tw-text-text">
          {row.name}
        </div>
        <div data-slot="onb-muted" className="tw-text-sm tw-text-text-muted">
          {docLine(row)}
        </div>
      </div>
      <Badge tone={row.status === "signed" ? "accent" : "neutral"}>
        {row.status === "signed" ? PILL_SIGNED : PILL_DELIVERED}
      </Badge>
    </li>
  );
}

// /onboarding/done — "That's the paperwork done" (spec § Receipt). Both lists
// iterate what the server returns, in its order (decision 13).
export default function ReceiptPage({
  readOnly = false,
  stage,
  resumed = false,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const go = useStageNavigation();
  const [receipt, setReceipt] = useState(null);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await getReceipt();
      setReceipt(res.data);
    } catch (_) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // No gate. The next stage is trial_start, whose web page is You're in
  // (routeFor.web '/welcome', SP4).
  const onContinue = async () => {
    if (readOnly) {
      go(stage);
      return;
    }
    setSending(true);
    try {
      const res = await continueOnboarding();
      go(res.data);
    } catch (err) {
      if (isWrongStage(err)) {
        go(err.response.data);
        return;
      }
      enqueueSnackbar(err?.response?.data?.message || GENERIC_ERROR, {
        variant: "error",
      });
      setSending(false);
    }
  };

  const side = (
    <>
      <p
        data-slot="onb-muted"
        className="tw-m-0 tw-text-sm tw-text-text-muted"
      >
        {RECEIPT_NEXT(stage?.intakePageCount)}
      </p>
      <Button disabled={sending} onClick={onContinue}>
        {RECEIPT_CONTINUE}
        <StarsIcon name="arrow" className="tw-h-4 tw-w-4 tw-shrink-0" />
      </Button>
      <Button
        variant="quiet"
        size="sm"
        className="tw-ml-auto"
        onClick={() => navigate("/documents")}
      >
        {RECEIPT_SEE_DOCUMENTS}
      </Button>
    </>
  );

  return (
    <OnboardingLayout
      railLabel={railLabelFor(readOnly ? null : stage?.rail)}
      sideTitle={RECEIPT_SIDE_TITLE}
      side={side}
      resumed={resumed}
    >
      <div
        data-slot="onb-receipt-title"
        className="tw-flex tw-items-center tw-gap-3"
      >
        <span
          data-slot="onb-bigcheck"
          className="tw-grid tw-h-12 tw-w-12 tw-shrink-0 tw-place-items-center tw-rounded-pill tw-bg-accent-tint tw-text-text-accent"
          aria-hidden="true"
        >
          <StarsIcon name="check" className="tw-h-6 tw-w-6" />
        </span>
        <h1
          data-slot="onb-h1"
          className="tw-m-0 tw-text-2xl tw-font-bold tw-text-text max-rail:tw-text-xl"
        >
          {RECEIPT_H1}
        </h1>
      </div>
      <p data-slot="onb-muted" className="tw-m-0 tw-text-md tw-text-text-muted">
        {RECEIPT_INTRO}
      </p>
      {failed && (
        <div role="alert" className="tw-text-sm tw-text-text">
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" size="sm" onClick={load}>
            {TRY_AGAIN}
          </Button>
        </div>
      )}
      {!failed && !receipt && <Loader />}
      {receipt && (
        <>
          <h2
            data-slot="onb-h3"
            className="tw-m-0 tw-text-lg tw-font-bold tw-text-text"
            id="onb-receipt-docs"
          >
            {RECEIPT_DOCS_HEADING}
          </h2>
          <Panel
            as="ul"
            data-slot="onb-receipt-list"
            className="tw-m-0 tw-list-none tw-py-0"
            aria-labelledby="onb-receipt-docs"
          >
            {receipt.documents.map((row) => (
              <React.Fragment key={`${row.kind}-${row.versionLabel}`}>
                <DocRow row={row} />
                {(row.children || []).map((child) => (
                  <DocRow
                    key={`${child.kind}-${child.name}`}
                    row={child}
                    child
                  />
                ))}
              </React.Fragment>
            ))}
          </Panel>
          <h2
            data-slot="onb-h3"
            className="tw-m-0 tw-text-lg tw-font-bold tw-text-text"
            id="onb-receipt-consents"
          >
            {RECEIPT_CONSENTS_HEADING}
          </h2>
          <Panel
            as="ul"
            data-slot="onb-receipt-list"
            className="tw-m-0 tw-list-none tw-py-0"
            aria-labelledby="onb-receipt-consents"
          >
            {receipt.consents.map((c) => (
              <li
                key={c.kind}
                data-slot="onb-receipt-row"
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-3 last:tw-border-b-0"
              >
                <span className="tw-text-sm tw-font-semibold tw-text-text">
                  {c.shortLabel}
                </span>
                <span
                  data-slot="onb-muted"
                  className="tw-text-sm tw-text-text-muted"
                >
                  {c.state && c.at ? fullDate(c.at) : RECEIPT_NOT_YET}
                </span>
              </li>
            ))}
          </Panel>
        </>
      )}
    </OnboardingLayout>
  );
}
